import { InternalWebRTCCallSideEffects } from "@components/internal-contacts/_components/InternalWebRTCCallSideEffects";
import { InternalWebRTCProvider } from "@components/internal-contacts/_context/InternalWebRTCContext";
import { InternalTwilioCallSideEffects } from "@components/internal-twilio-call/components/InternalTwilioCallSideEffects";
import { InternalTwilioCallProvider } from "@components/internal-twilio-call/context/InternalTwilioCallContext";
import { JobChatNotification } from "@components/job-chats/JobChatNotification";
import { AudioPlaybackCallGuard } from "@components/shared/components/audio-player/AudioPlaybackCallGuard";
import { SmsNotification } from "@components/virtual-receptionist/sms/SmsNotification";
import { ReleasesPage } from "@features/Releases";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react"; //App.js
import { ErrorBoundary } from "react-error-boundary";
import { useDispatch, useSelector } from "react-redux"; // Importa el Provider de Redux
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import "./App.css";
import { EProductSection } from "./commons/interfaces/product";
import { AdminInvitationValidate } from "./components/admins/AdminAcceptInvitation";
import ResponsiveAppBar from "./components/appbar/AppBar";
import { AppsCenterPage } from "./components/apps-integration/pages/AppCenterPage";
import { StripeOAuthCallback } from "./components/apps-integration/pages/StripeOAuthCallback";
import { HomeLayout } from "./components/bussiness/HomeLayout";
import { OrganizationLayout } from "./components/bussiness/OrganizationLayout";
import { ChatDock, ChatDockProvider, useChatDock } from "./components/chat-dock";
import { clearAllChatDrafts } from "./components/chat-dock/chatDrafts";
import { ClientsLayout } from "./components/clients/Clients.layout";
import { ClientsTable } from "./components/clients/clientsTable";
import { InvoicesTable } from "./components/invoice/DashboardWidget/InvoicesTable";
import { BaseModalComponent, GlobalModalStack } from "./components/common/BaseModalComponent";
import { NotificationsCenter } from "./components/common/NotificationsCenter";
import { NotificationSettings } from "./components/common/NotificationSettings";
import { getSideBarOptions } from "./components/common/SidebarToolkit/sidebar.config";
import { ConnectACleanerIndividualAction } from "./components/ConnectACleanerAction";
import { ConnectBusinessAccount } from "./components/ConnectBusinessAccount";
import { ConnectEmployeeAccount } from "./components/connectEmployeeAccount";
import { ConnectEmployeeLayout } from "./components/ConnectEmployeeLayout";
import { ConnectIndividualWelcomeMessage } from "./components/connectIndividualWelcomeMessage";
import { ConnectionRequests } from "./components/connectionRequests";
import CoveragePage from "./components/coverage-areas/components/principals/CoveragePage";
import { DeactivationMessage } from "./components/deactivationMessage";
import { EmailConfirmed } from "./components/EmailConfirmated";
import { EmailConfirmation } from "./components/EmailConfirmation";
import { EmailConfirmationWithPassword } from "./components/EmailConfirmationWithPassword";
import WorkforceCompanyDeletion from "./components/employees/components/WorkforceAccountDeletion";
import { ErrorHandler } from "./components/Errors/ErrorHandler";
import { RelatedDepositInvoiceModalHost } from "./components/invoice/RelatedDepositInvoiceModalHost";
import { ClientView } from "./components/invoice/view";
import { JobsList } from "./components/jobs/jobs-list/JobsList";
import MainLayout from "./components/layout/MainLayout";
import MainLayout2 from "./components/layout/MainLayout2";
import { PaymentsLayout } from "./components/layout/PaymentsLayout";
import { PlansLayout } from "./components/layout/PlansLayout";
import SigninupLayout from "./components/layout/SigninupLayout";
import { MessageGroupsSocketBridge } from "./components/message-groups";
import MobileAppDownloadPage from "./components/mobile/MobileAppPage";
import { PageBuilderAccessGate } from "./components/page-builder/access/PageBuilderAccessGate";
import { PageBuilderDashboard } from "./components/page-builder/dashboard/PageBuilderDashboard";
import { PageBuilderEditor } from "./components/page-builder/editor/PageBuilderEditor";
import { PageBuilderLoadingScreen } from "./components/page-builder/loading/PageBuilderLoadingScreen";
import PrivacyPolicy from "./components/privacy-policy";
import { ScopeOfWorkView } from "./components/quotes/quote-detail/summary/variants/advanced/view/ScopeOfWorkView";
import { QuoteView } from "./components/quotes/quote-detail/view/QuoteView";
import { ResetPassword } from "./components/resetPassword";
import { SignUpBusiness } from "./components/signup/business/SignUpBusiness";
import { SignupEntry } from "./components/signup/components/SignupEntry";
import { SignUpIndividual } from "./components/signup/individual/SignUpIndividual";
import { SignUpEmployee } from "./components/signupEmployee";
import { ForgotPassword } from "./components/singin/ForgotPassword";
import { SignIn } from "./components/singin/signin";
import { BillingSubscriptionsLayout } from "./components/superadmin/billing/BillingSubscriptionsLayout";
import { ContactCenterDashboard } from "./components/superadmin/contact-center";
import { UserSummaryTable } from "./components/Table";
import { TermsAndConditions } from "./components/termsAndConditions";
import { VendorsLayout } from "./components/vendors/Vendors.layout";
import { CallArbitrationProvider } from "./components/virtual-receptionist/calls/arbitration/CallArbitrationContext";
import { CallNotification } from "./components/virtual-receptionist/calls/CallNotification";
import { VirtualAssistantProvider } from "./components/virtual-receptionist/context/VirtualAssistantContext";
import { ContactCenterThemeProvider } from "./components/virtual-receptionist/theming/ContactCenterThemeContext";
import { VirtualReceptionistView } from "./components/virtual-receptionist/VirtualReceptionistView";
import { FloatingBubblesProvider } from "./context/floating-bubbles/FloatingBubblesContext";
import { useSocket } from "./context/SocketContext";
import { UnreadMessagesProvider } from "./context/unread-messages/UnreadMessagesContext";
import { AgendaInteligenteRoute } from "./features/agenda-inteligente/AgendaInteligenteRoute";
import { FlowHub } from "./features/FlowHub";
import { ValidateToken } from "./middlewares/validateToken";
import { BrowserNotificationBridge } from "./notifications/browser/BrowserNotificationBridge";
import { useWatchInvoiceNotifications } from "./notifications/hooks/useWatchInvoiceNotifications";
import { useWatchJobProgressNotifications } from "./notifications/hooks/useWatchJobInProgressNotifications";
import { NotificationCenter } from "./notifications/notification-center/NotificationCenter";
import AdminsPageLayout from "./pages/dashboard/AdminsPageLayout";
import { ConnectEmployeePage } from "./pages/dashboard/ConnectEmployeePage";
import DefaultPage from "./pages/dashboard/DefaultPage";
import EmployeePageLayout from "./pages/dashboard/EmployeePageLayout";
import { setAppState } from "./redux/features/appStateSlice";
import { selectRole } from "./redux/features/user/selectors/userState.selectors";
import { updateUser } from "./redux/features/userStateSlice";
import { useAppSelector } from "./redux/hooks/redux";
import { decodeToken, getBasePath, IRoles, setMetaDescription } from "./utils";

const ChatDockSessionCleaner = ({ token }) => {
  const { closeAllChats } = useChatDock();

  useEffect(() => {
    if (!token) {
      closeAllChats();
      clearAllChatDrafts();
    }
  }, [token, closeAllChats]);

  return null;
};

function App() {
  const path = "/";
  const queryClient = useQueryClient();

  const token = sessionStorage.getItem("token");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname } = useLocation();

  const isReleasesRoute = location.pathname.startsWith("/releases");

  const userInfo = token ? decodeToken(token) : null;
  const storedRole = useAppSelector(selectRole);
  const roleType = storedRole ?? userInfo?.roles?.[0]?.type;

  const userState = useSelector((state) => state.userState);

  const basePath = getBasePath(roleType);

  const { socket } = useSocket();

  const renderStableMainLayout = (child, layoutBasePath = basePath, validateTokenProps = {}, layoutProps = {}) => (
    <MainLayout2
      basePath={layoutBasePath}
      {...layoutProps}
      child={
        // ValidateToken queda dentro del contenido para que sidebar y navbar no se desmonten.
        <ValidateToken {...validateTokenProps}>{child}</ValidateToken>
      }
    />
  );

  useWatchJobProgressNotifications({
    onNotificationArrived: () => {
      queryClient.invalidateQueries(["job-events-no-pagination", "jobs"]);
    },
  });

  useWatchInvoiceNotifications({});

  useEffect(() => {
    const path = location.pathname;

    // 1. Lista blanca de rutas públicas
    const publicPaths = [
      "/terms-and-conditions",
      "/signin",
      "/signup",
      "/jobs",
      "/invitation",
      "/quotes",
      "/admin",
      "/business",
      "/flow-hub",
      "/privacy-policy",
      "/delete-account-request",
      "/invoices",
      "/page-builder",
      "/releases",
      "/contact-center",
    ];

    // 2. Definición de prefijos por rol
    const allowedPrefixes = {
      business: "/flow-hub",
      admin: "/flow-hub",
      accountManager: "/flow-hub",
      virtualReceptionist: "/flow-hub",
      employee: "/flow-hub",
      superadmin: "/admin",
      other: "/flow-hub",
    };

    const expectedPrefix = allowedPrefixes[roleType];

    // Let Stripe exchange the OAuth code before applying role-based navigation.
    if (path === "/stripe/oauth/callback") return;

    if (path === "/") {
      if (roleType && expectedPrefix) {
        navigate(expectedPrefix, { replace: true });
      } else {
        navigate("/signin", { replace: true });
      }
    }

    if (publicPaths.some((p) => path.startsWith(p))) {
      return;
    }

    if (!roleType) return;

    const hasCompanies = userState.companies && userState.companies.length > 0;

    if (!hasCompanies && path !== "/flow-hub" && userState.uid && roleType !== "admin") {
      navigate("/flow-hub", { replace: true });
      return;
    }

    if (expectedPrefix && !path.startsWith(expectedPrefix)) {
      navigate(expectedPrefix, { replace: true });
    }
  }, [roleType, location.pathname, navigate, userState.companies, userState.uid]);

  useEffect(() => {
    if (!socket) return;

    socket.on("messageRecord", (msg) => {
      console.log(" MessageRecord (App-level):", msg);
    });

    return () => socket.off("messageRecord");
  }, [socket]);

  useEffect(() => {
    dispatch(setAppState({ prevPathState: path, currPathState: pathname }));
    setMetaDescription();
  }, []);

  useEffect(() => {
    const requestBrowserNotificationPermission = async () => {
      if (!("Notification" in window)) {
        return "unsupported";
      }

      if (!window.isSecureContext) {
        return "insecure";
      }

      if (Notification.permission === "granted") {
        return "granted";
      }

      if (Notification.permission === "denied") {
        return "denied";
      }

      return await Notification.requestPermission();
    };

    requestBrowserNotificationPermission();
  }, []);

  return (
    <div className="App">
      <ErrorBoundary FallbackComponent={ErrorHandler}>
        <VirtualAssistantProvider>
          <UnreadMessagesProvider>
            <FloatingBubblesProvider>
              <InternalWebRTCProvider>
                <InternalTwilioCallProvider>
                  <ContactCenterThemeProvider>
                    <CallArbitrationProvider>
                      <ChatDockProvider>
                        <ChatDockSessionCleaner token={token} />
                        <InternalWebRTCCallSideEffects />
                        <InternalTwilioCallSideEffects />
                        <AudioPlaybackCallGuard />

                        <Routes>
                          <Route
                            path="/connectacleaner/individual/:token"
                            element={
                              <ValidateToken checkById={true} isUserToken={false}>
                                <SigninupLayout>
                                  <ConnectACleanerIndividualAction />
                                </SigninupLayout>
                              </ValidateToken>
                            }
                          />

                          {/* Validate Quote routes */}
                          <Route
                            path="/quotes/:id"
                            element={
                              <ValidateToken checkByParam={true}>
                                <QuoteView />
                              </ValidateToken>
                            }
                          />

                          <Route
                            path="/invoices/:invoiceUid"
                            element={
                              <ValidateToken checkByParam={true}>
                                <ClientView />
                              </ValidateToken>
                            }
                          />

                          <Route
                            path="/business/page-builder/loading"
                            element={
                              <ValidateToken checkByParam={false}>
                                <PageBuilderAccessGate>
                                  <PageBuilderLoadingScreen />
                                </PageBuilderAccessGate>
                              </ValidateToken>
                            }
                          />

                          <Route
                            path="/business/page-builder"
                            element={renderStableMainLayout(
                              <PageBuilderAccessGate>
                                <PageBuilderDashboard />
                              </PageBuilderAccessGate>,
                              basePath,
                              { checkByParam: false },
                            )}
                          />

                          <Route
                            path="/business/page-builder/editor/:draftId"
                            element={
                              <ValidateToken checkByParam={false}>
                                <PageBuilderAccessGate>
                                  <PageBuilderEditor />
                                </PageBuilderAccessGate>
                              </ValidateToken>
                            }
                          />

                          <Route
                            path="/signin"
                            element={
                              <ValidateToken>
                                <SigninupLayout>
                                  <ResponsiveAppBar />
                                  <SignIn />
                                </SigninupLayout>
                              </ValidateToken>
                            }
                          />

                          <Route
                            path="/quotes/:id/scope-of-work"
                            element={
                              <ValidateToken checkByParam={true}>
                                <ScopeOfWorkView />
                              </ValidateToken>
                            }
                          />

                          <Route
                            path="/deactivation/user/:token"
                            element={
                              <ValidateToken checkById={true} isUserToken={false}>
                                <SigninupLayout>
                                  <DeactivationMessage />
                                </SigninupLayout>
                              </ValidateToken>
                            }
                          />

                          <Route
                            path="/reset/:token"
                            element={
                              <ValidateToken checkById={true} isUserToken={false}>
                                <SigninupLayout>
                                  <ResetPassword />
                                </SigninupLayout>
                              </ValidateToken>
                            }
                          />

                          <Route
                            path="/join/user/:token"
                            element={
                              <ValidateToken checkById={true} isUserToken={false}>
                                <SigninupLayout>
                                  <EmailConfirmationWithPassword />
                                </SigninupLayout>
                              </ValidateToken>
                            }
                          />

                          {/* 
                    Se pretende tener varios urls de activation
                    "/activation/users/superadmin/:token": Activa una cuenta que tiene todos los accesos
                    "/activation/users/admin/:token": Activa una cuenta a Admin (Hecho por un SuperAdmin)
                    "/activation/users/employee/:token" "Activa una cuenta como employe de un Admin o Super Admin"
                  */}
                          <Route
                            path="/activation/users/:token"
                            element={
                              <ValidateToken>
                                <EmailConfirmed />
                              </ValidateToken>
                            }
                          />

                          <Route
                            path="/signin/forgotpassword"
                            element={
                              <SigninupLayout>
                                <ForgotPassword />
                              </SigninupLayout>
                            }
                          />

                          <Route
                            path="/signup"
                            element={
                              <ValidateToken>
                                <ResponsiveAppBar />
                                <SignupEntry />
                                <Routes>
                                  <Route path="/emailconfirmation" element={<EmailConfirmation />} />
                                </Routes>
                              </ValidateToken>
                            }
                          />

                          <Route
                            path="/signup/create-user"
                            element={
                              <ValidateToken>
                                <ResponsiveAppBar />
                                <SignupEntry />
                                <Routes>
                                  <Route path="/emailconfirmation" element={<EmailConfirmation />} />
                                </Routes>
                              </ValidateToken>
                            }
                          />

                          <Route
                            path="/signup/individual"
                            element={
                              <ValidateToken>
                                <ResponsiveAppBar />
                                <SignUpIndividual />
                                <Routes>
                                  <Route path="/emailconfirmation" element={<EmailConfirmation />} />
                                </Routes>
                              </ValidateToken>
                            }
                          />

                          <Route
                            path="/signup/business"
                            element={
                              <>
                                <SignUpBusiness />
                                <Routes>
                                  <Route path="/emailconfirmation" element={<EmailConfirmation />} />
                                </Routes>
                              </>
                            }
                          />

                          <Route
                            path="/signupEmployee"
                            element={
                              <SigninupLayout>
                                <SignUpEmployee />
                                <Routes>
                                  <Route path="/emailconfirmation" element={<EmailConfirmation />} />
                                </Routes>
                              </SigninupLayout>
                            }
                          />

                          <Route path="/" element={<Navigate to="/flow-hub" replace />} />

                          <Route path="/flow-hub" element={renderStableMainLayout(<FlowHub />)} />

                          <Route path="/business" element={renderStableMainLayout(<HomeLayout />)} />
                          <Route
                            path="/business/agenda-inteligente/*"
                            element={renderStableMainLayout(<AgendaInteligenteRoute />, basePath, {}, { hideSidebar: true })}
                          />

                          <Route
                            path="/workforce"
                            element={renderStableMainLayout(
                              roleType === "employee" && userState?.hasCoverageArea ? <ConnectIndividualWelcomeMessage /> : <HomeLayout />,
                            )}
                          />

                          <Route path={`${basePath}/organization`} element={renderStableMainLayout(<OrganizationLayout />)} />

                          <Route path={`${basePath}/apps-center`} element={renderStableMainLayout(<AppsCenterPage />)} />
                          <Route
                            path="/stripe/oauth/callback"
                            element={
                              <ValidateToken checkByParam={false}>
                                <StripeOAuthCallback />
                              </ValidateToken>
                            }
                          />

                          <Route path="/admin" element={renderStableMainLayout(<HomeLayout />)} />

                          <Route
                            path={`${basePath}/my-connections`}
                            element={renderStableMainLayout(<ConnectionRequests />, getBasePath(roleType))}
                          />

                          <Route
                            path={`${basePath}/my-clients`}
                            element={renderStableMainLayout(<ClientsLayout disableInnerChilds={true} />, getBasePath(roleType))}
                          />

                          <Route
                            path={`${basePath}/my-vendors`}
                            element={
                              <ValidateToken>
                                <MainLayout2 basePath={getBasePath(roleType)} child={<VendorsLayout disableInnerChilds={true} />} />
                              </ValidateToken>
                            }
                          />

                          <Route path={`${basePath}/jobs`} element={renderStableMainLayout(<JobsList />, getBasePath(roleType))} />
                          <Route
                            path={`${basePath}/clients`}
                            element={renderStableMainLayout(
                              <ClientsTable showContactsToggle paginationAlign="left" sx={{ p: { xs: 1, md: 3 }, height: "100%" }} />,
                              getBasePath(roleType),
                            )}
                          />

                          <Route path={`${basePath}/invoices`} element={renderStableMainLayout(<InvoicesTable />, getBasePath(roleType))} />

                          <Route
                            path={`${basePath}/settings-account`}
                            element={renderStableMainLayout(
                              roleType === "business" || roleType === "other" ? (
                                <ConnectBusinessAccount componentState={userState} updateComponentState={updateUser} />
                              ) : (
                                <ConnectEmployeeAccount componentState={userState} updateComponentState={updateUser} />
                              ),
                            )}
                          />

                          {/* 
                    <Route
                      path="/dashboard"
                      element={
                        <ValidateToken>
                          <MainLayout child={<DashboardPageLayout />} />
                        </ValidateToken>
                      }
                    />
                  */}

                          <Route
                            path="/businessprofile/admins"
                            element={
                              <ValidateToken>
                                <MainLayout child={<AdminsPageLayout />} />
                              </ValidateToken>
                            }
                          />

                          <Route
                            path="/businessprofile/employees"
                            element={
                              <ValidateToken>
                                <MainLayout child={<EmployeePageLayout />} />
                              </ValidateToken>
                            }
                          />

                          <Route
                            path="/businessprofile/employees/:id"
                            element={
                              <ValidateToken>
                                <MainLayout child={<EmployeePageLayout />} />
                              </ValidateToken>
                            }
                          />

                          <Route
                            path="/businessprofile/clients"
                            element={
                              <ValidateToken>
                                <MainLayout child={<DefaultPage />} />
                              </ValidateToken>
                            }
                          />

                          {/* Connect a cleaner business routes */}
                          <Route path={`${basePath}/coverage-areas`} element={renderStableMainLayout(<CoveragePage />)} />

                          <Route path={`${basePath}/notifications-center`} element={renderStableMainLayout(<NotificationsCenter />)} />

                          {/* 
                        <Route
                          path={`${basePath}/notifications-settings`}
                          element={
                            <ValidateToken>
                              <MainLayout2 child={<NotificationSettings />} />
                            </ValidateToken>
                          }
                        />
                        */}

                          <Route path={`${basePath}/work-setup`} element={renderStableMainLayout(<ConnectEmployeeLayout />)} />

                          <Route
                            path={`${basePath}/plan-center`}
                            element={renderStableMainLayout(
                              roleType === IRoles.SuperAdmin ? (
                                <BillingSubscriptionsLayout />
                              ) : (
                                <PlansLayout section={EProductSection.FLOW_SUBSCRIPTION} />
                              ),
                            )}
                          />

                          <Route path={`${basePath}/contact-center`} element={renderStableMainLayout(<ContactCenterDashboard />)} />
                          <Route path="/contact-center" element={renderStableMainLayout(<ContactCenterDashboard />)} />

                          <Route
                            path={`${basePath}/settings`}
                            element={
                              <ValidateToken>
                                <MainLayout child={<DefaultPage />} />
                              </ValidateToken>
                            }
                          />

                          <Route
                            path={`${basePath}/reports`}
                            element={
                              <ValidateToken>
                                <MainLayout child={<DefaultPage />} />
                              </ValidateToken>
                            }
                          />

                          <Route
                            path={`${basePath}/tickets`}
                            element={
                              <ValidateToken>
                                <MainLayout child={<DefaultPage />} />
                              </ValidateToken>
                            }
                          />

                          <Route
                            path={`${basePath}/connect`}
                            element={
                              <ValidateToken>
                                <MainLayout child={<ConnectEmployeePage />} />
                              </ValidateToken>
                            }
                          />

                          {/* Super admin routes */}
                          <Route path="/sales" element={renderStableMainLayout(<NotificationSettings />)} />

                          <Route
                            path="/total-registered-users"
                            element={renderStableMainLayout(<UserSummaryTable currPage="/total-registered-users" title="Total Registered Users" />)}
                          />

                          <Route
                            path="/total-registered-individuals"
                            element={renderStableMainLayout(
                              <UserSummaryTable currPage="/total-registered-individuals" typeState="employee" title="Total Registered Individuals" />,
                            )}
                          />

                          <Route
                            path="/total-registered-business"
                            element={renderStableMainLayout(
                              <UserSummaryTable currPage="/total-registered-business" typeState="business" title="Total Registered Business" />,
                            )}
                          />

                          <Route
                            path="/total-deleted-individuals"
                            element={renderStableMainLayout(
                              <UserSummaryTable
                                currPage="/total-deleted-individuals"
                                isDeleted={true}
                                typeState="employee"
                                title="Total Deleted Individuals"
                              />,
                            )}
                          />

                          <Route
                            path="/total-deleted-business"
                            element={renderStableMainLayout(
                              <UserSummaryTable
                                currPage="/total-deleted-business"
                                isDeleted={true}
                                typeState="business"
                                title="Total Deleted Business"
                              />,
                            )}
                          />

                          <Route
                            path={`${basePath}/payments`}
                            element={renderStableMainLayout(<PaymentsLayout currPage={`${basePath}/payments`} />)}
                          />

                          {/* 
                    <Route
                      path={`${basePath}/calculator`}
                      element={
                        <ValidateToken>
                          <MainLayout2 basePath={basePath} child={<BasicCalculatorLayout />} />
                        </ValidateToken>
                      }
                    />
                  */}

                          <Route path="/terms-and-conditions" element={<TermsAndConditions openInNewPage={true} />} />

                          <Route
                            path="/privacy-policy"
                            element={token ? <Navigate to="/business?modals=Privacy-policys" replace /> : <PrivacyPolicy />}
                          />

                          <Route path="/mobile" element={<MobileAppDownloadPage />} />
                          <Route path="/releases" element={<ReleasesPage />} />
                          <Route path="/delete-account-request" element={<WorkforceCompanyDeletion />} />
                          <Route path="/invitation" element={<AdminInvitationValidate />} />

                          <Route path="*" element={<Navigate to="/" />} />
                        </Routes>

                        <CallNotification vibrationMode="vibrate" />
                        {!isReleasesRoute && <VirtualReceptionistView />}
                        {!isReleasesRoute && <ChatDock />}
                        {!isReleasesRoute && <MessageGroupsSocketBridge />}

                        <NotificationCenter />
                        <BrowserNotificationBridge />
                        <SmsNotification />
                        <JobChatNotification />

                        {/* <ContactsView /> */}

                        {getSideBarOptions.map((item) => (
                          <BaseModalComponent key={item.key} modalKey={item.modalKey} modalLabel={item.label} defaultSizes={{ width: 700 }}>
                            {item.content}
                          </BaseModalComponent>
                        ))}

                        <RelatedDepositInvoiceModalHost />
                        <GlobalModalStack />
                      </ChatDockProvider>
                    </CallArbitrationProvider>
                  </ContactCenterThemeProvider>
                </InternalTwilioCallProvider>
              </InternalWebRTCProvider>
            </FloatingBubblesProvider>
          </UnreadMessagesProvider>
        </VirtualAssistantProvider>
      </ErrorBoundary>
    </div>
  );
}

export default App;
